import FileIconExcel from '@/assets/img/ai-chat/research-file-type/excel.svg';
import FileIconFile from '@/assets/img/ai-chat/research-file-type/file.svg';
import FileIconHtml from '@/assets/img/ai-chat/research-file-type/html.svg';
import FileIconImage from '@/assets/img/ai-chat/research-file-type/image.svg';
import FileIconMd from '@/assets/img/ai-chat/research-file-type/md.svg';
import FileIconPdf from '@/assets/img/ai-chat/research-file-type/pdf.svg';
import FileIconPpt from '@/assets/img/ai-chat/research-file-type/ppt.svg';
import FileIconWord from '@/assets/img/ai-chat/research-file-type/word.svg';
import FolderIconCustom from '@/assets/img/ai-chat/research-file-type/folder-custom.svg';
import FolderIconDefault from '@/assets/img/ai-chat/research-file-type/folder-default.svg';
import FolderIconIndustry from '@/assets/img/ai-chat/research-file-type/folder-industry.svg';
import FolderIconStock from '@/assets/img/ai-chat/research-file-type/folder-stock.svg';

import { LOCAL_IMPORT_EXTENSION_GROUPS } from '../leftPanel/constants/fileExtensions';
import { EDITOR_ENGINE_TIPTAP } from '../middleEditor/editor/editorEngine';

const RESEARCH_FILE_ICON_RULES = [
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.markdown, icon: FileIconMd },
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.word, icon: FileIconWord },
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.excel, icon: FileIconExcel },
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.ppt, icon: FileIconPpt },
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.image, icon: FileIconImage },
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.html, icon: FileIconHtml },
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.pdf, icon: FileIconPdf }
];

/** 文件类型字符串规则：与 RESEARCH_FILE_ICON_RULES 一一对应，用于埋点等需要类型字符串（非图标）的场景 */
const RESEARCH_FILE_TYPE_RULES = [
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.markdown, type: 'md' },
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.word, type: 'word' },
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.excel, type: 'excel' },
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.ppt, type: 'ppt' },
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.image, type: 'image' },
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.html, type: 'html' },
  { extensions: LOCAL_IMPORT_EXTENSION_GROUPS.pdf, type: 'pdf' }
];

/** 未命中扩展名规则时的兜底文件类型（与通用文件图标 FileIconFile 对应） */
const RESEARCH_FILE_TYPE_FALLBACK = 'file';

const RESEARCH_NODE_TYPE_FOLDER = 1;
const RESEARCH_TASK_TYPE_STOCK = 1;
const RESEARCH_TASK_TYPE_INDUSTRY = 2;

export const RESEARCH_FOLDER_ICON_TYPE = Object.freeze({
  DEFAULT: 'default',
  STOCK: 'stock',
  INDUSTRY: 'industry',
  CUSTOM: 'custom'
});

export const RESEARCH_FOLDER_ICON_MAP = Object.freeze({
  [RESEARCH_FOLDER_ICON_TYPE.DEFAULT]: FolderIconDefault,
  [RESEARCH_FOLDER_ICON_TYPE.STOCK]: FolderIconStock,
  [RESEARCH_FOLDER_ICON_TYPE.INDUSTRY]: FolderIconIndustry,
  [RESEARCH_FOLDER_ICON_TYPE.CUSTOM]: FolderIconCustom
});

/**
 * 解析研究文件扩展名，统一为不含点号的小写格式。
 * @param {object} fileInfo 研究文件信息（含 ext）
 * @returns {string} 不含点号的小写扩展名；无扩展名时为空字符串
 */
export function resolveResearchFileExt(fileInfo) {
  const ext = String(fileInfo?.ext || '').toLowerCase();
  // 兼容接口返回带点号的扩展名（如 ".pdf"）
  return ext.startsWith('.') ? ext.slice(1) : ext;
}

/**
 * 根据研究文件信息返回对应的文件图标；未命中扩展名规则时兜底为通用文件图标。
 * @param {object} fileInfo 研究文件信息（含 ext / editorEngine）
 * @returns {string} 文件图标资源地址
 */
export function resolveResearchFileIcon(fileInfo) {
  return resolveResearchMatchedFileIcon(fileInfo) ?? FileIconFile;
}

/**
 * 按扩展名 / 编辑器引擎匹配文件图标；未命中时返回 undefined，由上层决定兜底。
 * @param {object} fileInfo 研究文件信息（含 ext / editorEngine）
 * @returns {string|undefined} 匹配到的图标资源，未匹配为 undefined
 */
function resolveResearchMatchedFileIcon(fileInfo) {
  if (Number(fileInfo?.editorEngine) === EDITOR_ENGINE_TIPTAP) return FileIconMd;
  const ext = resolveResearchFileExt(fileInfo);
  return RESEARCH_FILE_ICON_RULES.find(({ extensions }) => extensions.includes(ext))?.icon;
}

/**
 * 解析研究文件类型字符串（与 resolveResearchFileIcon 同源规则），如 md / word / excel / ppt / image / html / pdf。
 * 命中 Tiptap 引擎按 md；未命中扩展名规则时回落到原始扩展名，仍无则为通用 'file'。
 * @param {object} fileInfo 研究文件信息（含 ext / editorEngine）
 * @returns {string} 文件类型字符串
 */
export function resolveResearchFileType(fileInfo) {
  // Tiptap 文档统一视为 Markdown，与图标解析口径保持一致
  if (Number(fileInfo?.editorEngine) === EDITOR_ENGINE_TIPTAP) return 'md';
  const ext = resolveResearchFileExt(fileInfo);
  return (
    RESEARCH_FILE_TYPE_RULES.find(({ extensions }) => extensions.includes(ext))?.type ??
    (ext || RESEARCH_FILE_TYPE_FALLBACK)
  );
}

/**
 * 根据研究目录节点信息返回对应的文件夹图标类型。
 * @param {object} folderInfo 研究目录节点信息（含 level / taskType）
 * @returns {string} RESEARCH_FOLDER_ICON_TYPE 中的类型值
 */
export function resolveResearchFolderIconType(folderInfo) {
  if (Number(folderInfo?.level) !== 1) return RESEARCH_FOLDER_ICON_TYPE.DEFAULT;
  if (Number(folderInfo?.taskType) === RESEARCH_TASK_TYPE_STOCK) return RESEARCH_FOLDER_ICON_TYPE.STOCK;
  if (Number(folderInfo?.taskType) === RESEARCH_TASK_TYPE_INDUSTRY) return RESEARCH_FOLDER_ICON_TYPE.INDUSTRY;
  return RESEARCH_FOLDER_ICON_TYPE.CUSTOM;
}

/**
 * 根据研究目录节点信息返回对应的文件夹图标。
 * @param {object} folderInfo 研究目录节点信息
 * @returns {string} 文件夹图标资源地址
 */
export function resolveResearchFolderIcon(folderInfo) {
  return RESEARCH_FOLDER_ICON_MAP[resolveResearchFolderIconType(folderInfo)];
}

/**
 * 根据研究模式树节点信息返回对应图标（文件夹或文件，含未知类型兜底）。
 * @param {object} nodeInfo 树节点信息（含 nodeType 等）
 * @returns {string} 图标资源地址
 */
export function resolveResearchNodeIcon(nodeInfo) {
  if (Number(nodeInfo?.nodeType) === RESEARCH_NODE_TYPE_FOLDER) return resolveResearchFolderIcon(nodeInfo);
  return resolveResearchFileIcon(nodeInfo);
}

/**
 * 根据研究模式树节点信息返回类型字符串（与 resolveResearchNodeIcon 同源）。
 * 文件夹返回 folder-<类型>（default/stock/industry/custom）；文件走 resolveResearchFileType。
 * @param {object} nodeInfo 树节点信息（含 nodeType 等）
 * @returns {string} 类型字符串，如 md / ppt / excel / folder-stock
 */
export function resolveResearchNodeType(nodeInfo) {
  // 文件夹需保留业务目录类型，便于埋点区分默认、个股、行业与自定义目录
  if (Number(nodeInfo?.nodeType) === RESEARCH_NODE_TYPE_FOLDER) {
    return `folder-${resolveResearchFolderIconType(nodeInfo)}`;
  }
  return resolveResearchFileType(nodeInfo);
}
